document.documentElement.classList.remove('no-js');

const bodyMoneyFormat = document.body?.dataset?.moneyFormat || '£{{amount}}';
let activeDrawerToggle = null;
let lastFocusedBeforeDrawer = null;

function formatMoneyFromCents(cents) {
  const amount = (Number(cents) || 0) / 100;
  const formatted = amount.toFixed(2);

  if (bodyMoneyFormat.includes('{{amount_no_decimals}}')) {
    return bodyMoneyFormat.replace('{{amount_no_decimals}}', Math.round(amount).toString());
  }

  if (bodyMoneyFormat.includes('{{amount_with_comma_separator}}')) {
    return bodyMoneyFormat.replace('{{amount_with_comma_separator}}', formatted.replace('.', ','));
  }

  return bodyMoneyFormat.replace('{{amount}}', formatted);
}

function getMobileDrawer() {
  return document.getElementById('MobileNavDrawer');
}

function openMobileDrawer(toggle) {
  const drawer = getMobileDrawer();
  if (!drawer) return;

  lastFocusedBeforeDrawer = document.activeElement;
  activeDrawerToggle = toggle || activeDrawerToggle;
  drawer.hidden = false;
  drawer.setAttribute('aria-hidden', 'false');
  document.body.classList.add('drawer-open');
  if (activeDrawerToggle) activeDrawerToggle.setAttribute('aria-expanded', 'true');

  const firstFocusable = drawer.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
  if (firstFocusable) firstFocusable.focus();
}

function closeMobileDrawer() {
  const drawer = getMobileDrawer();
  if (!drawer) return;

  drawer.hidden = true;
  drawer.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('drawer-open');
  if (activeDrawerToggle) activeDrawerToggle.setAttribute('aria-expanded', 'false');
  if (lastFocusedBeforeDrawer && typeof lastFocusedBeforeDrawer.focus === 'function') {
    lastFocusedBeforeDrawer.focus();
  }
}

function trapDrawerFocus(event) {
  const drawer = getMobileDrawer();
  if (!drawer || drawer.hidden || event.key !== 'Tab') return;

  const focusable = Array.from(
    drawer.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
  ).filter((node) => !node.hasAttribute('disabled'));

  if (!focusable.length) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function setActiveMedia(root, mediaId) {
  if (!root || !mediaId) return;

  root.querySelectorAll('[data-product-media-item]').forEach((item) => {
    const isMatch = String(item.dataset.mediaId) === String(mediaId);
    item.hidden = !isMatch;
    item.classList.toggle('is-active', isMatch);
  });

  root.querySelectorAll('[data-product-thumb]').forEach((thumb) => {
    const isMatch = String(thumb.dataset.mediaId) === String(mediaId);
    thumb.classList.toggle('is-active', isMatch);
    if (isMatch) {
      thumb.setAttribute('aria-current', 'true');
    } else {
      thumb.removeAttribute('aria-current');
    }
  });
}

document.addEventListener('click', function (event) {
  const toggle = event.target.closest('[data-mobile-nav-toggle]');
  if (toggle) {
    const drawer = getMobileDrawer();
    if (!drawer) return;
    if (drawer.hidden) {
      openMobileDrawer(toggle);
    } else {
      closeMobileDrawer();
    }
    return;
  }

  if (event.target.closest('[data-mobile-nav-close]') || event.target.matches('[data-mobile-nav-overlay]')) {
    closeMobileDrawer();
    return;
  }

  const thumb = event.target.closest('[data-product-thumb]');
  if (thumb) {
    const root = thumb.closest('[data-product-root]');
    setActiveMedia(root, thumb.getAttribute('data-media-id'));
    return;
  }

  const accordionButton = event.target.closest('.product-accordion__button');
  if (accordionButton) {
    const content = accordionButton.nextElementSibling;
    const expanded = accordionButton.getAttribute('aria-expanded') === 'true';
    accordionButton.setAttribute('aria-expanded', String(!expanded));
    if (content) content.hidden = expanded;
  }
});

document.addEventListener('keydown', function (event) {
  if (event.key === 'Escape') {
    closeMobileDrawer();
  }
  trapDrawerFocus(event);
});

function setupProductForms() {
  document.querySelectorAll('[data-product-form]').forEach((form) => {
    const root = form.closest('[data-product-root]');
    const variantsScript = form.querySelector('[data-product-variants]');
    const optionInputs = Array.from(form.querySelectorAll('[data-product-option]'));
    const variantIdInput = form.querySelector('[data-variant-id]');
    const addToCartButton = form.querySelector('[data-add-to-cart]');
    const sellingPlanInput = form.querySelector('[data-selling-plan-id]');
    const isGoldClub = Boolean(addToCartButton?.dataset.goldClubCta);
    const priceNode = root?.querySelector('[data-product-price]');
    const statusNode = form.querySelector('[data-product-status]');

    if (!variantsScript || !variantIdInput) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (error) {
      return;
    }

    const getSelectedVariant = () => {
      if (!optionInputs.length) return variants[0];
      const selectedOptions = optionInputs.map((input) => input.value);
      return variants.find((variant) =>
        variant.options.every((optionValue, index) => optionValue === selectedOptions[index])
      );
    };

    const updateVariantState = () => {
      const variant = getSelectedVariant();
      if (!variant) {
        if (statusNode) statusNode.textContent = 'This combination is unavailable.';
        if (addToCartButton) {
          addToCartButton.disabled = true;
          addToCartButton.textContent = 'Unavailable';
        }
        return;
      }

      variantIdInput.value = variant.id;
      if (sellingPlanInput) {
        const firstSellingPlanAllocation = Array.isArray(variant.selling_plan_allocations) ? variant.selling_plan_allocations[0] : null;
        const sellingPlanId = firstSellingPlanAllocation && (firstSellingPlanAllocation.selling_plan_id || firstSellingPlanAllocation.selling_plan?.id);
        if (sellingPlanId) {
          sellingPlanInput.value = sellingPlanId;
        }
      }
      if (priceNode && typeof variant.price !== 'undefined') {
        priceNode.textContent = formatMoneyFromCents(variant.price);
      }
      if (variant.featured_media && variant.featured_media.id) {
        setActiveMedia(root, variant.featured_media.id);
      }
      if (statusNode) {
        statusNode.textContent = variant.available
          ? (isGoldClub ? 'Subscription ready to join.' : 'Ready to add to basket.')
          : 'Currently sold out.';
      }
      if (addToCartButton) {
        addToCartButton.disabled = !variant.available;
        addToCartButton.textContent = variant.available ? (isGoldClub ? 'Join Gold Club' : 'Add to cart') : 'Sold out';
      }
    };

    optionInputs.forEach((input) => input.addEventListener('change', updateVariantState));
    updateVariantState();
  });
}

setupProductForms();
